import {Button} from '@chekinapp/ui';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {usePath, useFetchOffers, useFetchDeals, useFetchGuruWalkTours} from 'hooks';
import {PATHS} from 'routes/constants';
import Page from 'components/Page';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {CategoryFilter, CategoryValue} from 'components/upselling/CategoryFilter';
import {OFFER_CATEGORIES, OFFER_STATUSES, LANGUAGE_CODES} from '@chekin/core';
import {createDealStatusMap} from './createDealStatusMap';
import {
  Content,
  DesktopActionPanel,
  ListWrapper,
  MobileActionPanel,
  StyledOfferCard,
  StyledGuruWalkOfferCard,
} from './styled';

const GURUWALK_AFFILIATE = import.meta.env.VITE_GURUWALK_AFFILIATE;
const GURUWALK_ORIGIN = 'https://www.guruwalk.com';

const SUPPORTED_GURUWALK_LANGUAGES = [
  LANGUAGE_CODES.en,
  LANGUAGE_CODES.es,
  LANGUAGE_CODES.de,
  LANGUAGE_CODES.it,
];

const getGuruWalkLanguage = (lang: string) => {
  const appLanguage = lang.slice(0, 2);
  if (SUPPORTED_GURUWALK_LANGUAGES.includes(appLanguage as LANGUAGE_CODES)) {
    return appLanguage;
  }

  return LANGUAGE_CODES.en;
};

const getCityOrCountry = (location?: {city?: string; country?: {name?: string}}) => {
  return location?.city || location?.country?.name;
};

const buildGuruWalkOfferUrl = ({
  tourPath,
  reservationId,
}: {
  tourPath: string;
  reservationId?: string;
}) => {
  const affiliate = GURUWALK_AFFILIATE || '';

  try {
    const url = new URL(tourPath, GURUWALK_ORIGIN);
    url.searchParams.set('ref', affiliate);

    if (reservationId) {
      url.searchParams.set('referrer_campaign', reservationId);
    }

    url.searchParams.set('utm_source', 'affiliates');
    url.searchParams.set('utm_medium', 'affiliates_widget');
    url.searchParams.set('utm_campaign', affiliate);

    return url.toString();
  } catch (_error) {
    return GURUWALK_ORIGIN;
  }
};

function OfferListView() {
  const {t, i18n} = useTranslation();
  const {getFullPath} = usePath();
  const {data: reservation} = useReservation();
  const {data: summary} = useSummary();
  const [selectedCategory, setSelectedCategory] = React.useState<CategoryValue>(null);

  const isFreeOffersSelected = selectedCategory === OFFER_CATEGORIES.freeOffers;
  const apiCategory = isFreeOffersSelected ? null : selectedCategory;

  const {data: paginatedOfferList, isLoading} = useFetchOffers({
    housingId: reservation?.housing_id,
    category: apiCategory,
  });

  const {data: paginatedDealsList} = useFetchDeals({reservationId: reservation?.id});
  const dealStatusMap = React.useMemo(
    () => createDealStatusMap(paginatedDealsList),
    [paginatedDealsList],
  );
  const hasOffersWithDeal = dealStatusMap.size > 0;

  const offers = React.useMemo(() => {
    return paginatedOfferList?.results ?? [];
  }, [paginatedOfferList]);

  const showGuruWalkByCategory = selectedCategory === null || isFreeOffersSelected;
  const isGuruWalkActive =
    summary?.guruwalk_status === OFFER_STATUSES.active && Boolean(summary?.location);
  const guruWalkLocality = isGuruWalkActive
    ? getCityOrCountry(summary?.location)
    : undefined;
  const guruWalkLanguage = React.useMemo(
    () => getGuruWalkLanguage(i18n.language),
    [i18n.language],
  );
  const {data: guruWalkTours = []} = useFetchGuruWalkTours({
    locality: showGuruWalkByCategory ? guruWalkLocality ?? '' : '',
    country: summary?.location?.country?.name ?? '',
    checkIn: summary?.check_in_date ?? '',
    checkOut: summary?.check_out_date ?? '',
    language: showGuruWalkByCategory && guruWalkLocality ? guruWalkLanguage : '',
    limit: 10,
  });
  const showGuruWalkOffers =
    showGuruWalkByCategory && Boolean(guruWalkLocality) && guruWalkTours.length > 0;

  const navigate = useNavigate();
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);

  const navigateToCart = () => {
    navigate(getFullPath(PATHS.payments.main));
  };

  const showRegularOffers = !isFreeOffersSelected;

  return (
    <Page
      isLoading={isLoading}
      isSubHeaderVisible
      onBackButtonClick={() => navigate(getFullPath(PATHS.home))}
      title={t('offer_list_view.subtitle')}
      navBarTitle={t('offer_list_view.nav_bar_title')}
      bottomStickyPanel={
        hasOffersWithDeal && isMobileMode ? (
          <MobileActionPanel>
            <Button onClick={navigateToCart}>{t('continue_t')}</Button>
          </MobileActionPanel>
        ) : null
      }
    >
      <Content>
        <CategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
        {(showRegularOffers || showGuruWalkOffers) && (
          <ListWrapper>
            {showRegularOffers &&
              offers.map(offer => (
                <StyledOfferCard
                  key={offer.id}
                  offer={offer}
                  status={dealStatusMap.get(offer.id)}
                />
              ))}
            {showGuruWalkOffers &&
              guruWalkTours.map((tour, index) => {
                const rating =
                  tour.testimonialsCount && tour.ratingAverage
                    ? `${tour.ratingAverage} (${tour.testimonialsCount})`
                    : null;

                return (
                  <StyledGuruWalkOfferCard
                    key={tour.id || tour.url || `${summary?.id}_${index}`}
                    href={buildGuruWalkOfferUrl({
                      tourPath: tour.url,
                      reservationId: reservation?.id,
                    })}
                    title={tour.title}
                    highlight={tour.description}
                    imageUrl={tour.imageUrl}
                    price={
                      rating
                        ? t('guruwalk.rating', {
                            rating,
                            defaultValue: 'Rating: {{rating}}',
                          })
                        : null
                    }
                    categoryLabel={t('offer_categories.free_offers')}
                  />
                );
              })}
          </ListWrapper>
        )}

        {!isMobileMode && hasOffersWithDeal && (
          <DesktopActionPanel>
            <Button onClick={navigateToCart}>{t('continue_t')}</Button>
          </DesktopActionPanel>
        )}
      </Content>
    </Page>
  );
}

export {OfferListView};
